import { type JSX } from 'react';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import { type PdfInsightSentiment, type PdfInsightsPageData } from '../benchmarkingReport.types';
import { ReportPage } from '../components/ReportPage';
import {
  PDF_BORDER_RADIUS,
  PDF_FONT_WEIGHTS,
  PDF_FONTS,
  PDF_PAGE,
  PDF_SPACING,
  PDF_TEXT_STYLES,
} from '../lib/pdfTokens';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TEXT_PRIMARY = '#25252A';
const TEXT_SECONDARY = '#5E5E69';
const CARD_BG = '#FFFFFF';
const CARD_BORDER = '#D9D9E3';

// Elo feedback token resolved hex values (light mode)
// TODO: When the real API is live, confirm whether additional sentimento variants
// (beyond 'information' and 'warning') are needed. The Figma page 10 shows at
// least 3 distinct card colors — this needs to be aligned with the backend.
const TONE_COLORS: Record<PdfInsightSentiment, { bg: string; text: string; iconColor: string }> = {
  // cyan.light.100 / cyan.light.1200
  information: { bg: '#D6F1FF', text: '#004D73', iconColor: '#004D73' },
  // yellow.light.100 / yellow.light.1300
  warning: { bg: '#FFF5DB', text: '#614700', iconColor: '#614700' },
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const COL_GAP = PDF_SPACING[3]; // 12pt gap between columns
const COL_WIDTH = (PDF_PAGE.contentWidth - COL_GAP) / 2;

const styles = StyleSheet.create({
  // ── Page header (outside any card) ────────────────────────────────────────
  titleBlock: { marginBottom: PDF_SPACING[2] },
  title: { ...PDF_TEXT_STYLES.pageTitle, marginBottom: 4 },
  introText: { ...PDF_TEXT_STYLES.body, maxWidth: PDF_PAGE.contentWidth * 0.6, marginBottom: PDF_SPACING[4] },

  // ── 2-column grid ─────────────────────────────────────────────────────────
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: COL_GAP,
  },

  // ── Individual insight card ───────────────────────────────────────────────
  card: {
    width: COL_WIDTH,
    backgroundColor: CARD_BG,
    borderRadius: PDF_BORDER_RADIUS.md,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 2,
    borderLeftWidth: 1,
    borderStyle: 'solid',
    borderColor: CARD_BORDER,
    padding: PDF_SPACING[3],
    flexDirection: 'column',
    gap: PDF_SPACING[3],
  },

  // ── Card header: icon box + heading ───────────────────────────────────────
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PDF_SPACING[2],
  },
  iconBox: {
    width: 28,
    height: 28,
    borderRadius: PDF_BORDER_RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardHeading: {
    flex: 1,
    fontSize: 9,
    fontWeight: PDF_FONT_WEIGHTS.bold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    lineHeight: 1.4,
  },

  // ── Body text ─────────────────────────────────────────────────────────────
  cardBody: {
    fontSize: 8.5,
    fontWeight: 300,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_SECONDARY,
    lineHeight: 1.5,
  },

  // ── "Direção estratégica" box ─────────────────────────────────────────────
  estrategiaBox: {
    borderRadius: PDF_BORDER_RADIUS.sm,
    padding: PDF_SPACING[2],
    flexDirection: 'column',
    gap: PDF_SPACING[1],
  },
  estrategiaHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PDF_SPACING[1],
  },
  estrategiaLabel: {
    fontSize: 8.5,
    fontWeight: PDF_FONT_WEIGHTS.semiBold,
    fontFamily: PDF_FONTS.sans,
    lineHeight: 1.35,
  },
  estrategiaText: {
    fontSize: 8,
    fontWeight: 300,
    fontFamily: PDF_FONTS.sans,
    lineHeight: 1.5,
  },
});

// ---------------------------------------------------------------------------
// InsightCard sub-component
// ---------------------------------------------------------------------------

type InsightCardProps = {
  turma: string;
  titulo: string;
  corpo: string;
  direcaoEstrategica: string;
  sentimento: PdfInsightSentiment;
  iconUri?: string;
  lightningIconUri?: string;
};

function InsightCard({
  turma,
  titulo,
  corpo,
  direcaoEstrategica,
  sentimento,
  iconUri,
  lightningIconUri,
}: InsightCardProps): JSX.Element {
  const tone = TONE_COLORS[sentimento];

  return (
    <View
      style={styles.card}
      wrap={false}>
      {/* Header: face icon + "Turma X: Topic" */}
      <View style={styles.cardHeader}>
        {iconUri ? (
          <View style={[styles.iconBox, { backgroundColor: tone.bg }]}>
            <Image
              src={iconUri}
              style={{ width: 20, height: 20 }}
            />
          </View>
        ) : null}
        <Text style={styles.cardHeading}>
          {turma}: {titulo}
        </Text>
      </View>

      {/* Main insight paragraph */}
      <Text style={styles.cardBody}>{corpo}</Text>

      {/* Direção estratégica */}
      <View style={[styles.estrategiaBox, { backgroundColor: tone.bg }]}>
        <View style={styles.estrategiaHeader}>
          {lightningIconUri ? (
            <Image
              src={lightningIconUri}
              style={{ width: 12, height: 12 }}
            />
          ) : null}
          <Text style={[styles.estrategiaLabel, { color: tone.text }]}>Direção estratégica</Text>
        </View>
        <Text style={[styles.estrategiaText, { color: tone.text }]}>{direcaoEstrategica}</Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type InsightsPageProps = {
  pageData: PdfInsightsPageData;
  lightningIconUri?: string;
};

export function InsightsPage({ pageData, lightningIconUri }: InsightsPageProps): JSX.Element {
  return (
    <ReportPage>
      <Text style={styles.title}>Insights que orientam decisões</Text>
      <Text style={styles.introText}>
        Os insights apresentados sintetizam leituras recorrentes que emergem da integração entre desempenho pedagógico e
        contexto competitivo, apoiando decisões com maior clareza e menor dependência de intuição.
      </Text>

      <View style={styles.grid}>
        {pageData.items.map((item) => (
          <InsightCard
            key={item.turmaId}
            turma={item.turma}
            titulo={item.titulo}
            corpo={item.corpo}
            direcaoEstrategica={item.direcaoEstrategica}
            sentimento={item.sentimento}
            iconUri={item.iconUri}
            lightningIconUri={lightningIconUri}
          />
        ))}
      </View>
    </ReportPage>
  );
}
